/**
 * TaskBadge — header "My Tasks" alert/badge (issue #1680, epic #1674).
 *
 * Shows the current user's open Task count (assigned to them directly or to
 * any team they belong to — see `useMyOpenTasks`). Clicking navigates to the
 * My Tasks list (`/tasks`). Renders nothing for an unauthenticated caller or
 * a caller with zero open Tasks — this is an alert surface, not a permanent
 * chrome element (matches the issue's "in-app alert only" scoping).
 */

import { useNavigate } from '@tanstack/react-router';
import { ForgeIcon, ForgeIconButton } from '@tylertech/forge-react';
import '@tylertech/forge/icon';
import '@tylertech/forge/icon-button';
import { useMyOpenTasks } from '../../graphql/queries/task';
import { useAppSelector } from '../../store';
import { selectUser } from '../../store/features/auth/authSlice';
import './TaskBadge.css';

export function TaskBadge(): JSX.Element | null {
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const { tasks, loading } = useMyOpenTasks(user?.id, user?.teams ?? []);

  if (!user || loading || tasks.length === 0) {
    return null;
  }

  const overdueCount = tasks.filter((task) => task.overdue).length;
  const label =
    overdueCount > 0
      ? `My Tasks: ${tasks.length} open, ${overdueCount} overdue`
      : `My Tasks: ${tasks.length} open`;

  return (
    <ForgeIconButton
      className={`task-badge${overdueCount > 0 ? ' task-badge--overdue' : ''}`}
      onClick={() => void navigate({ to: '/tasks' })}
    >
      <button type="button" aria-label={label} data-testid="task-badge">
        <ForgeIcon name="assignment" aria-hidden="true" />
        <span className="task-badge__count" data-testid="task-badge-count">
          {tasks.length}
        </span>
      </button>
    </ForgeIconButton>
  );
}
